import React, { useCallback } from 'react';
import { useField } from 'formik';
import { ValueType } from 'react-select';
import { FormikErrorMessage } from './FormikErrorMessage';
import { FormikLabel } from './FormikLabel';
import { Dropdown } from 'lib/components';
import { Flex, FlexProps } from '../styles/layout';
import { CSSProperties } from 'styled-components/macro';
import { debounce } from 'lodash';
export interface FormikDropdownOption {
  value: string | number | null;
  label: string;
  type?: string;
  __isNew__?: string;
}
export type OptionValue = ValueType<FormikDropdownOption, false>;

interface CustomDropdownProps {
  name: string;
  label?: string;
  isRequired?: boolean;
  options: FormikDropdownOption[];
  disabled?: boolean;
  readonly?: boolean;
  onChange?: (option: OptionValue) => void;
  onCreateOption?: (inputValue: string) => void;
  extendStyles?: {
    errorWrapper?: CSSProperties;
    labelStyles?: CSSProperties;
    asteriskStyles?: CSSProperties;
    inputWrapper?: FlexProps;
    mainWrapper?: FlexProps;
  };
  info?: string | React.ReactNode;
  creatable?: boolean;
  placeholder?: string;
  menuPortalTarget?: HTMLElement | null;
  hideError?: boolean;
  onMenuScrollToBottom?: () => void;
  isSearchable?: boolean;
  onSearchInputChange?: (query: string) => void;
  isLoading?: boolean;
  isClearable?: boolean;
}

export const FormikDropdownField = ({
  label,
  isRequired = false,
  options,
  disabled = false,
  readonly = false,
  onChange,
  extendStyles,
  info,
  creatable = false,
  placeholder = 'Select...',
  menuPortalTarget = document.body,
  onCreateOption,
  hideError = false,
  onMenuScrollToBottom,
  isSearchable = false,
  onSearchInputChange,
  isLoading = false,
  isClearable = false,
  ...props
}: CustomDropdownProps) => {
  const [field, meta, helpers] = useField(props);
  const selectedValue = options.find(option => option.value === field.value);

  const handleChange = (option: OptionValue) => {
    if (!!onChange) {
      onChange(option);
      return;
    }
    helpers.setValue(option ? option.value : '');
  };

  const debouncedInputChange = useCallback(
    debounce(inputValue => {
      onSearchInputChange && onSearchInputChange(inputValue);
    }, 500),
    []
  );

  const onInputChange = (input: string, actionMeta: { action: string }) => {
    // run debounced search only if the action is input-change to avoid unnecessary calls like item selection
    if (actionMeta.action === 'input-change' || actionMeta.action === 'clear') {
      debouncedInputChange(input);
    }
  };

  return (
    <Flex {...extendStyles?.mainWrapper} gap='4px'>
      {label && (
        <FormikLabel
          label={label}
          isRequired={isRequired}
          disabled={!!disabled}
          info={info}
          labelStyles={extendStyles?.labelStyles}
          asteriskStyles={extendStyles?.asteriskStyles}
        />
      )}
      <Flex {...extendStyles?.inputWrapper} gap='4px'>
        <Dropdown
          disabled={disabled}
          readonly={readonly}
          value={selectedValue}
          onChange={handleChange}
          placeholder={placeholder}
          onMenuOpen={() => helpers.setTouched(true)}
          onMenuScrollToBottom={onMenuScrollToBottom}
          options={options}
          menuPortalTarget={menuPortalTarget}
          inputId={props.name}
          creatable={creatable}
          onCreateOption={onCreateOption}
          isSearchable={isSearchable}
          onInputChange={onInputChange}
          isLoading={isLoading}
          isClearable={isClearable}
        />
        {!hideError && <FormikErrorMessage meta={meta} />}
      </Flex>
    </Flex>
  );
};
